import { Controller } from "@hotwired/stimulus"
import { applyKey, autoShift } from "../lib/on_screen_keyboard"

// UI-16: types into the focused `[data-keyboard="on-screen"]` input. Keys use
// pointerdown with preventDefault so the input never loses focus.
export default class extends Controller {
  static targets = ["row", "shift", "letter"]

  connect() {
    this.shift = false
    this.onFocusIn = (event) => this.track(event.target)
    this.onInput = (event) => { if (event.target === this.input) this.syncShift() }
    document.addEventListener("focusin", this.onFocusIn)
    document.addEventListener("input", this.onInput)
    this.track(document.activeElement)
  }

  disconnect() {
    document.removeEventListener("focusin", this.onFocusIn)
    document.removeEventListener("input", this.onInput)
    this.stopRepeat()
  }

  press(event) {
    event.preventDefault()
    this.type(event.params.key)
  }

  startRepeat(event) {
    event.preventDefault()
    this.stopRepeat()
    this.type("backspace")
    this.repeatDelay = setTimeout(() => {
      this.repeatTimer = setInterval(() => this.type("backspace"), 80)
    }, 500)
  }

  stopRepeat() {
    clearTimeout(this.repeatDelay)
    clearInterval(this.repeatTimer)
  }

  // private

  track(element) {
    if (element?.matches?.('[data-keyboard="on-screen"]')) {
      this.input = element
      this.syncShift()
    }
  }

  type(key) {
    const input = this.input
    if (!input || !input.isConnected) return

    const length = input.value.length
    const next = applyKey({
      value: input.value,
      start: input.selectionStart ?? length,
      end: input.selectionEnd ?? length,
      shift: this.shift,
      capitalize: input.dataset.capitalize || "none",
      maxLength: input.maxLength > 0 ? input.maxLength : null
    }, key)

    if (next.value !== input.value) {
      input.value = next.value
      input.setSelectionRange(next.start, next.end)
      input.dispatchEvent(new Event("input", { bubbles: true }))
    } else {
      input.setSelectionRange(next.start, next.end)
    }

    this.shift = next.shift
    if (document.activeElement !== input) input.focus()
    this.render()
  }

  syncShift() {
    const input = this.input
    const caret = input.selectionStart ?? input.value.length
    this.shift = autoShift(input.value, caret, input.dataset.capitalize || "none")
    this.render()
  }

  render() {
    this.letterTargets.forEach((key) => {
      const letter = key.dataset.onScreenKeyboardKeyParam
      key.textContent = this.shift ? letter.toUpperCase() : letter
    })
    if (this.hasShiftTarget) {
      this.shiftTarget.setAttribute("aria-pressed", this.shift)
      this.shiftTarget.classList.toggle("bg-violet-200", this.shift)
      this.shiftTarget.classList.toggle("bg-white", !this.shift)
    }
  }
}
